import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { isTokenExpired } from "../utils/auth";
import { emitTokenExpired } from "../utils/authEvents";

/**
 * Oturum interceptor'ları (upstream `357274f`).
 *
 * Upstream bu mantığı iki ayrı dosyada tekrarlıyor (`setupAxios.ts` global axios için,
 * `apiClient.ts` kendi instance'ı için). Burada tek yerde tanımlanıp iki instance'a da
 * takılıyor — `axios.create()` ile üretilen instance'lar varsayılan axios'un
 * interceptor'larını **miras almaz**, bu yüzden ikisine de ayrı ayrı kaydetmek gerekiyor.
 */

/**
 * Oturum açma/kapama uçları. Bunlar interceptor'dan **muaf**: aksi hâlde süresi dolmuş
 * token localStorage'da dururken
 *   - yeniden giriş isteği (`Login`, `LoginWithKeycloak`) reddedilir ve kullanıcı
 *     tarayıcıyı temizlemeden bir daha giremez,
 *   - `logOut()` içindeki `LogoutWithKeycloak` çağrısı da reddedileceği için Keycloak
 *     tarafındaki oturum **hiç kapanmaz** ve dönen 401 aynı akışı tekrar tetikler.
 * Upstream'de bu muafiyet yok; ürün kurulumlarımızda Keycloak açık olabildiği için eklendi.
 */
const AUTH_ENDPOINTS = [
  "api/Login/Login",
  "api/Home/LoginWithKeycloak",
  "api/Home/LogoutWithKeycloak",
];

const isAuthEndpoint = (url?: string): boolean =>
  !!url && AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

const onRequest = (config: InternalAxiosRequestConfig) => {
  if (isAuthEndpoint(config.url)) {
    return config;
  }

  const token = localStorage.getItem("token");

  if (token && isTokenExpired(token)) {
    emitTokenExpired();
    return Promise.reject(new Error("Token süresi doldu, oturum kapatıldı."));
  }

  if (token && !config.headers.get("Authorization")) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  return config;
};

const onResponseError = (error: AxiosError) => {
  if (error.response?.status === 401 && !isAuthEndpoint(error.config?.url)) {
    emitTokenExpired();
  }

  return Promise.reject(error);
};

/** Verilen axios instance'ına oturum interceptor'larını takar. */
export const attachSessionInterceptors = (instance: AxiosInstance): void => {
  instance.interceptors.request.use(onRequest);
  instance.interceptors.response.use((response) => response, onResponseError);
};

let isConfigured = false;

/** Varsayılan axios instance'ını yapılandırır; `src/index.tsx` içinde bir kez çağrılır. */
export const configureAxios = (): void => {
  if (isConfigured) {
    return;
  }

  attachSessionInterceptors(axios);
  isConfigured = true;
};
